import { FC } from "preact/compat";
import { useCallback, useEffect, useLayoutEffect, useRef } from "preact/hooks";
import { useComputed, useSignal, useSignalEffect } from "@preact/signals";
import { Document, Page, pdfjs } from "react-pdf";
import { useDebounce } from "~/hooks";
import FigureItem from "./FigureItem";
import HoldKbd from "./HoldKbd";
import ProcessAnimation from "./ProcessAnimation";
import { MediaViewerProps } from "./interfaces";
import { getAnimationParams, renderImageArea, renderPdfArea } from "./utils";
import cn from "classnames";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.js";

const DEFAULT_ZOOM_SCALE = 2;

const MediaViewer: FC<MediaViewerProps> = ({
    type,
    src,
    alt,
    activeIndex,
    className,
    loading,
    scalable: initialScalable,
    onZoomAction,
    zoomScale = DEFAULT_ZOOM_SCALE,
    resetKey,
    figures,
    pageSize,
    processing,
}) => {
    const loaded = useSignal(type === "pdf");
    const canBeScaled = useSignal(type === "pdf");
    const zoomingByKbd = useSignal(false);
    const zoomingByBtn = useSignal(false);
    const zooming = useComputed(() => zoomingByKbd.value || zoomingByBtn.value);
    const area = useSignal({
        x: 0,
        y: 0,
        width: 0,
        height: 0,
    });
    const imgRef = useRef<HTMLImageElement>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
    const lastMouse = useRef({ x: -1, y: -1 });
    const canvasReady = useRef(false);
    const lastNotifiedZooming = useRef(false);

    const rootRef = useRef<HTMLDivElement>(null);
    const previewRef = useRef<HTMLDivElement>(null);
    const previewSize = useRef({ width: 0, height: 0 });
    const size = useSignal({ width: 0, height: 0 });
    const debouncedSize = useDebounce(size.value, 200);
    const numPages = useSignal(0);
    const previewReady = useRef(false);

    const refCallback = useCallback((el: HTMLCanvasElement | null) => {
        if (el) {
            canvasRef.current = el;
            ctxRef.current = el.getContext("2d");
        }
    }, []);

    const setupPreview = useCallback(() => {
        const isPdf = type === "pdf";
        const elem = isPdf ? previewRef.current : canvasRef.current;
        const readyRef = isPdf ? previewReady : canvasReady;
        const parent = isPdf
            ? (rootRef.current?.parentElement ?? rootRef.current)
            : imgRef.current;

        if (readyRef.current || !elem || !parent) {
            return;
        }

        const s = size.peek();
        const width = Math.min(
            isPdf ? s.width * zoomScale : (imgRef.current?.naturalWidth ?? 0),
            window.innerWidth * 0.4,
        );
        const height = Math.min(
            isPdf ? s.height * zoomScale : (imgRef.current?.naturalHeight ?? 0),
            window.innerHeight * 0.6,
        );
        const rect = parent.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;

        if (elem instanceof HTMLCanvasElement) {
            elem.width = width;
            elem.height = height;
        }

        elem.style.width = `${width}px`;
        elem.style.height = `${height}px`;

        if (cx > innerWidth / 2) {
            elem.style.left = `${rect.left - width - 16}px`;
        } else {
            elem.style.left = `${rect.right + 16}px`;
        }

        const y = Math.min(
            Math.max(8, rect.top + rect.height / 2 - height / 2),
            window.innerHeight - height - 8,
        );

        if (isPdf) {
            previewSize.current = { width, height };
        }

        elem.style.top = `${y}px`;
        readyRef.current = true;
    }, [type, zoomScale, size]);

    const handleMouseMove = useCallback(
        (event: MouseEvent) => {
            lastMouse.current = { x: event.offsetX, y: event.offsetY };

            if (type === "pdf") {
                if (loading?.peek() || !previewRef.current || !zooming.peek()) {
                    return;
                }

                setupPreview();

                area.value = renderPdfArea(
                    previewRef.current,
                    previewSize.current,
                    lastMouse.current.x,
                    lastMouse.current.y,
                    zoomScale,
                    size.peek(),
                );
            } else {
                if (
                    !loaded.peek() ||
                    !imgRef.current ||
                    !canvasRef.current ||
                    !ctxRef.current ||
                    !zooming.peek()
                ) {
                    return;
                }

                const img = imgRef.current;
                const canvas = canvasRef.current;
                const ctx = ctxRef.current;

                setupPreview();

                area.value = renderImageArea(
                    canvas,
                    ctx,
                    img,
                    event.offsetX,
                    event.offsetY,
                );
            }

            zoomingByKbd.value = true;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [type, zoomScale, setupPreview],
    );

    const handleMouseLeave = useCallback(() => {
        zoomingByKbd.value = false;
        zoomingByBtn.value = false;
        lastMouse.current = { x: -1, y: -1 };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleMouseEnter = useCallback((e: MouseEvent) => {
        if (e.ctrlKey || e.metaKey) {
            zoomingByKbd.value = true;
            lastMouse.current = { x: e.offsetX, y: e.offsetY };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useSignalEffect(() => {
        const isZooming = zooming.value;

        if (!onZoomAction || isZooming === lastNotifiedZooming.current) {
            return;
        }

        onZoomAction(isZooming);
        lastNotifiedZooming.current = isZooming;
    });

    useLayoutEffect(() => {
        canBeScaled.value = type === "pdf";
        loaded.value = type === "pdf";
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [type]);

    useEffect(() => {
        if (type === "pdf") {
            const root = rootRef.current;

            if (!root) {
                return;
            }

            size.value = {
                width: root.clientWidth,
                height: root.clientHeight,
            };
            debouncedSize.value = {
                width: root.clientWidth,
                height: root.clientHeight,
            };
            previewReady.current = false;
        } else {
            // @TODO: should ResizeObserver be used here?
            canvasReady.current = false;

            const img = imgRef.current;

            if (img) {
                canBeScaled.value =
                    img.naturalWidth > img.width ||
                    img.naturalHeight > img.height;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [type, resetKey]);

    useEffect(() => {
        zoomingByKbd.value = false;
        zoomingByBtn.value = false;
        canvasReady.current = false;
    }, [src, zoomingByKbd, zoomingByBtn]);

    useEffect(() => {
        const onKeyUp = () => {
            if (zooming.peek()) {
                zoomingByKbd.value = false;
            }
        };

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape" && zoomingByBtn.peek()) {
                zoomingByBtn.value = false;
                return;
            }

            if (
                (event.key !== "Control" && event.key !== "Meta") ||
                !initialScalable ||
                !canBeScaled.peek() ||
                zooming.peek() ||
                lastMouse.current.x === -1 ||
                lastMouse.current.y === -1
            ) {
                return;
            }

            setupPreview();

            if (type === "pdf") {
                if (!previewRef.current) {
                    return;
                }

                area.value = renderPdfArea(
                    previewRef.current,
                    previewSize.current,
                    lastMouse.current.x,
                    lastMouse.current.y,
                    zoomScale,
                    size.peek(),
                );
            } else {
                const img = imgRef.current;
                const canvas = canvasRef.current;
                const ctx = ctxRef.current;

                if (!img || !canvas || !ctx) {
                    return;
                }

                area.value = renderImageArea(
                    canvas,
                    ctx,
                    img,
                    lastMouse.current.x,
                    lastMouse.current.y,
                );
            }

            zoomingByKbd.value = true;
        };

        const onResize = () => {
            previewReady.current = false;
        };

        window.addEventListener("keyup", onKeyUp);
        window.addEventListener("keydown", onKeyDown);
        window.addEventListener("resize", onResize);

        return () => {
            window.removeEventListener("keyup", onKeyUp);
            window.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("resize", onResize);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [type, initialScalable, zoomScale]);

    const scalable = initialScalable && canBeScaled.value;
    const animParams = getAnimationParams(type, pageSize, size, imgRef.current);

    return (
        <>
            {scalable && loaded.value && (
                <HoldKbd
                    signal={zoomingByBtn}
                    onActivate={() => {
                        if (
                            lastMouse.current.x === -1 ||
                            lastMouse.current.y === -1
                        ) {
                            lastMouse.current = {
                                x: 0,
                                y: 0,
                            };
                        }

                        let newArea: (typeof area)["value"];

                        setupPreview();

                        if (type === "pdf") {
                            const preview = previewRef.current;

                            if (!preview) {
                                return;
                            }

                            newArea = renderPdfArea(
                                preview,
                                previewSize.current,
                                lastMouse.current.x,
                                lastMouse.current.y,
                                zoomScale,
                                size.value,
                            );
                        } else {
                            const img = imgRef.current;
                            const canvas = canvasRef.current;
                            const ctx = ctxRef.current;

                            if (!img || !canvas || !ctx) {
                                return;
                            }

                            newArea = renderImageArea(
                                canvas,
                                ctx,
                                img,
                                lastMouse.current.x,
                                lastMouse.current.y,
                            );
                        }

                        area.value = newArea;
                    }}
                />
            )}
            <div
                ref={rootRef}
                className={cn("relative w-full h-full", className)}
            >
                <ProcessAnimation
                    active={processing ?? false}
                    imgSize={animParams.img}
                    offset={animParams.offset}
                />
                {scalable && type === "pdf" && (
                    <div
                        ref={previewRef}
                        className={cn(
                            "fixed z-[52] select-none overflow-hidden border-2 border-sky-500 bg-white",
                            !zooming.value && "hidden",
                        )}
                    >
                        <div data-document-wrapper>
                            <Document file={src} loading={<></>}>
                                <Page
                                    pageIndex={activeIndex.value}
                                    scale={zoomScale}
                                    width={debouncedSize.value.width}
                                    loading={<></>}
                                />
                            </Document>
                        </div>
                    </div>
                )}
                {scalable && type === "image" && (
                    <canvas
                        ref={refCallback}
                        className={cn(
                            "fixed z-[52] border-2 border-sky-500",
                            zooming.value ? "block" : "hidden",
                        )}
                    />
                )}
                {type === "pdf" && (
                    <Document
                        key={`${src}-${numPages.value}`}
                        file={src}
                        onLoadSuccess={(pdf) => {
                            numPages.value = pdf.numPages + 1;

                            if (
                                activeIndex.peek() >= pdf.numPages ||
                                activeIndex.peek() < 0
                            ) {
                                activeIndex.value = 0;
                            }

                            if (loading) {
                                loading.value = false;
                            }
                        }}
                        loading={<></>}
                        onMouseMove={scalable ? handleMouseMove : undefined}
                        onMouseLeave={scalable ? handleMouseLeave : undefined}
                    >
                        <Page
                            className={cn(
                                zooming.value && "pointer-events-none",
                            )}
                            pageIndex={activeIndex.value}
                            width={debouncedSize.value.width}
                            loading={<></>}
                            onLoadSuccess={(p) => {
                                size.value = {
                                    width: p.width,
                                    height: p.height,
                                };
                                debouncedSize.value = {
                                    width: p.width,
                                    height: p.height,
                                };

                                if (loading) {
                                    loading.value = false;
                                }
                            }}
                        />
                    </Document>
                )}
                {type === "image" && (
                    <img
                        ref={imgRef}
                        onLoad={(e) => {
                            loaded.value = true;
                            canBeScaled.value =
                                e.currentTarget.naturalWidth >
                                    e.currentTarget.width ||
                                e.currentTarget.naturalHeight >
                                    e.currentTarget.height;

                            if (loading) {
                                loading.value = false;
                            }
                        }}
                        onError={() => {
                            loaded.value = false;
                            canBeScaled.value = false;

                            if (loading) {
                                loading.value = false;
                            }
                        }}
                        onMouseMove={scalable ? handleMouseMove : undefined}
                        onMouseLeave={scalable ? handleMouseLeave : undefined}
                        onMouseEnter={scalable ? handleMouseEnter : undefined}
                        onDragStart={(e) => e.preventDefault()}
                        src={src}
                        alt={alt}
                        className="max-h-full max-w-full object-cover"
                    />
                )}
                {zooming.value && (
                    <div
                        style={{
                            left: area.value.x,
                            top: area.value.y,
                            width: area.value.width,
                            height: area.value.height,
                        }}
                        className="pointer-events-none absolute border-2 border-sky-500 bg-sky-100/40"
                    />
                )}
                {(figures?.length ?? 0) > 0 && (
                    <svg
                        style={{
                            width: animParams.img.width,
                            height: animParams.img.height,
                            left: animParams.offset.x,
                            top: animParams.offset.y,
                        }}
                        className="pointer-events-none absolute"
                        viewBox={`0 0 ${animParams.page.width} ${animParams.page.height}`}
                    >
                        {figures?.map((figure, idx) => (
                            <FigureItem
                                key={idx}
                                figure={figure}
                                pageSize={animParams.page}
                                imgSize={animParams.img}
                                offset={animParams.offset}
                            />
                        ))}
                    </svg>
                )}
            </div>
        </>
    );
};

export default MediaViewer;
